import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

interface AddInvoiceCouponParams {
  invoiceId: string;
  value: number;
}

export interface AddInvoiceCouponResponse {
  message: string;
  invoice: {
    _id: string;
    baseAmount: number;
    finalAmount: number;
    appliedCoupons: Array<{
      _id: string;
      name: string;
      type: string;
      category: string;
      duration: string;
      value: number;
      isActive: boolean;
      restrictions: Record<string, unknown>;
      metadata: Record<string, unknown>;
    }>;
    totalDiscount: number;
    newDiscountAdded: number;
  };
}

export const addInvoiceCoupon = async ({
  invoiceId,
  value,
}: AddInvoiceCouponParams) => {
  const response = await baseAPI.post<AddInvoiceCouponResponse>(
    `subscription-invoices/coupons/${invoiceId}`,
    { value },
  );
  return response.data;
};

export const useAddInvoiceCoupon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addInvoiceCoupon,
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [KEYS.INVOICE_COUPONS, variables.invoiceId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.PAYMENT_TRANSACTION_LINE_ITEMS, variables.invoiceId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.UPCOMING_SUBSCRIPTION_INVOICES],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.TUITION_DETAILS],
      });
      // Coupon affects final/outstanding → refresh Dashboard V2 AR
      void queryClient.invalidateQueries({ queryKey: [KEYS.ACCOUNTS_RECEIVABLE] });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.ACCOUNTS_RECEIVABLE_DUE_DATE_GROUPING],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.ACCOUNTS_RECEIVABLE_STUDENT_GROUPING],
      });
      // Transaction History / Recent Activity / balance show the discount too
      void queryClient.invalidateQueries({
        queryKey: [KEYS.STUDENT_PAYMENT_HISTORY],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.PARENT_PAYMENTS_HISTORY],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.OUTSTANDING_BALANCE],
      });
      toast.success('Coupon added successfully');
    },
    onError: () => {
      toast.error('Failed to add coupon');
    },
  });
};
